import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import axios, { CancelToken } from "axios";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";

import { GetLeague } from "api/rpc/2024-04/facilityAdmin/league/league";

import { showError } from "redux/actions/ui";
import { ILeague } from "redux/reducers/models/league";

import { useAppDispatch } from "hooks/redux";
import { displayCurrency } from "helpers/Helpers";

import Page from "components/page/Page";
import { IPrizeAccount } from "pages/secure/facility/settings/prizeAccounts/PrizeAccount";
import DataTable from "components/dataTable/DataTable";
import { GetCreditBook } from "api/rpc/2024-04/facilityAdmin/client/admin/creditBook";
import Callout from "components/callout/Callout";
import { Badge } from "components/badge/Badge";

export default function LeaguePrizeAccounts() {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { leagueId }: any = useParams();

  const [prizeAccounts, setPrizeAccounts] = useState<IPrizeAccount[]>(undefined);

  const [leagues, setLeagues] = useState<Array<ILeague>>([]);

  useEffect(() => {
    const source = axios.CancelToken.source();

    void loadPrizeAccounts(source.token);
    void loadLeagues(source.token);

    return () => source.cancel();
  }, []);

  async function loadPrizeAccounts(token?: CancelToken) {
    if (prizeAccounts !== undefined) {
      setPrizeAccounts(undefined);
    }
    const { status, data } = await GetCreditBook(
      { type: "prize_account", league_id: leagueId },
      token ? false : true,
      token,
    );

    if (token && token.reason) {
      return;
    }
    if (status !== StatusCode.OK) {
      dispatch(showError(t("Error loading prize accounts")));
    }

    const filteredData = data.filter((prizeAccount: any) => Boolean(prizeAccount.closed) !== true);
    setPrizeAccounts(status !== StatusCode.OK ? [] : filteredData);
  }

  async function loadLeagues(token?: CancelToken) {
    const { status, data } = await GetLeague(null, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (status !== StatusCode.OK) {
      dispatch(showError(t("Error loading leagues")));
      return;
    }

    setLeagues(data);
  }

  const primaryAction = {
    content: "New Prize Account",
    action: () => history.push("/admin/league/" + String(leagueId) + "/prizeAccounts/new"),
  };

  return (
    <>
      <Page title={t("Prize Accounts")} primaryAction={primaryAction} narrow>
        {prizeAccounts?.length > 0 ? (
          <DataTable
            columns={[{ label: t("Name") }, { label: t("Event") }, { label: t("Balance") }, { label: "" }]}
            loading={prizeAccounts === undefined}
          >
            {prizeAccounts?.map(account => {
              const prizeLeague = leagues.filter(league => league.id === account.league_id);

              return (
                <tr
                  key={account.id}
                  onClick={() =>
                    history.push("/admin/league/" + String(leagueId) + "/prizeAccounts/view/" + String(account.id))
                  }
                  className="clickable"
                >
                  <td>
                    <p>{account.title}</p>
                  </td>
                  <td>
                    <p>{prizeLeague[0] ? prizeLeague[0].name : ""}</p>
                  </td>

                  <td>
                    <p>{displayCurrency("cad", account.balance)}</p>
                  </td>

                  <td>{account.closed ? <Badge type="error">{t('Closed')}</Badge>:<Badge type="success">{t('Open')}</Badge>}</td>
                </tr>
              );
            })}
          </DataTable>
        ) : (
          <>
            <Callout title={t("No Prize Accounts Found")} content={t("You do not have any prize accounts")} type="info" />
          </>
        )}
      </Page>
    </>
  );
}
